<template>
  <div class="card mb-4">
    <div class="card-header">
      {{ isEditing ? $t("Edit User") : $t("Create a New User") }}
    </div>
    <div class="card-body">
        <b-form @submit.prevent="handleSubmit">
            <b-row>
                <b-col md="4">
                    <b-form-group
                        label="First Name"
                        :state="getFieldState('first_name')"
                        invalid-feedback="First name is required"
                    >
                        <b-form-input
                            v-model="form.first_name"
                            :state="getFieldState('first_name')"
                            required
                        ></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group
                        label="Last Name"
                        :state="getFieldState('last_name')"
                        invalid-feedback="Last name is required"
                    >
                        <b-form-input
                            v-model="form.last_name"
                            :state="getFieldState('last_name')"
                            required
                        ></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group
                        label="Email Address"
                        :state="getFieldState('email')"
                        invalid-feedback="Email is required"
                    >
                        <b-form-input
                            type="email"
                            v-model="form.email"
                            :state="getFieldState('email')"
                            required
                        ></b-form-input>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="4">
                    <b-form-group
                        label="Language"
                        :state="getFieldState('language_id')"
                        invalid-feedback="Please select a language"
                    >
                        <b-form-select
                            v-model="form.language_id"
                            :options="formattedLanguages"
                            :state="getFieldState('language_id')"
                        ></b-form-select>
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group
                        label="Phone Number"
                        :state="getFieldState('phone_number')"
                        invalid-feedback="Invalid phone number"
                    >
                        <b-form-input
                            type="text"
                            v-model="form.phone_number"
                            :state="getFieldState('phone_number')"
                        ></b-form-input>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="4">
                    <b-form-group
                        label="Company"
                        :state="getFieldState('company_id')"
                        invalid-feedback="Please select a company"
                    >
                        <b-form-select
                            v-model="form.company_id"
                            :options="formattedCompanies"
                            :state="getFieldState('company_id')"
                        ></b-form-select>
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group
                        label="User Role"
                        :state="getFieldState('role_id')"
                        invalid-feedback="Please select a role"
                    >
                      <b-form-select
                        v-model="form.role"
                        :options="formattedRoles"
                        :state="getFieldState('role')"
                        required
                      ></b-form-select>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-form-checkbox v-model="form.is_active" name="is_active">
                User is active
            </b-form-checkbox>

            <b-button :disabled="loading" variant="primary" type="submit">
                <span v-if="loading" class="spinner-border spinner-border-sm" role="status"></span>
                {{ isEditing ? "Save" : "Create User" }}
            </b-button>
        </b-form>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "UserForm",
  props: {
    user: {
      type: Object,
      default: () => ({})
    },
    languages: {
      type: Array,
      default: () => []
    },
    companies: {
      type: Object,
      default: () => []
    },
    roles: {
      type: Array,
      default: () => []
    },
    formAction: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      form: {
        first_name: this.user.first_name || "",
        last_name: this.user.last_name || "",
        email: this.user.email || "",
        phone_number: this.user.phone_number || "",
        language_id: this.user.language_id || "",
        company_id: this.user.company_id || "",
        role: this.user.role || "",
        is_active:
          this.user.is_active !== undefined ? !!this.user.is_active : true
      },
      errors: {},
      loading: false
    };
  },
  computed: {
    isEditing() {
      return !!this.user.id; // If `id` exists, it's an update
    },
    formattedCompanies() {
      // Convert companies into a list with optgroups
      return Object.entries(this.companies).map(([group, list]) => ({
        label: `${group.charAt(0).toUpperCase()}${group.slice(1)}s`,
        options: list.map(company => ({
          value: company.id,
          text: company.name
        }))
      }));
    },
    formattedLanguages() {
      return this.languages.map(language => ({
        value: language.id,
        text: language.name || "Unknown"
      }));
    },
    formattedRoles() {
      return this.roles.map(role => ({
        value: role.name,
        text: role.name || "Unknown"
      }));
    }
  },
  methods: {
    getFieldState(field) {
      return this.errors[field] === undefined ? null : false;
    },
    async handleSubmit() {
      this.loading = true;
      this.errors = {};

      try {
        const role = this.roles.find(r => r.name === this.form.role);
        const payload = {
          ...this.form,
          role_id: role ? role.id : null
        };

        const method = this.isEditing ? "put" : "post";

        await axios[method](this.formAction, payload);

        this.$bvToast.toast(
          this.isEditing
            ? "User updated successfully!"
            : "User created successfully!",
          {
            title: "Success",
            variant: "success",
            solid: true
          }
        );

        setTimeout(() => {
          window.location.href = "/users";
        }, 1000);
      } catch (error) {
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.$bvToast.toast("An error occurred. Please try again.", {
            title: "Error",
            variant: "danger",
            solid: true
          });
        }
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
